/**
 * SubdomainRouter
 * ===============
 * Detects tenant subdomains (e.g., the-bounce-squad.partyrentalsuite.com).
 * When detected, resolves the tenant via API and renders the tenant's
 * token-driven public site at clean root URLs.
 *
 * Phase 6 (2026-04-27): the 9 hardcoded React themes were retired.
 * Every tenant now renders through SiteRouterV2 (the token-driven engine).
 * This file's job is reduced to: detect subdomain → resolve tenant →
 * apply the draft gate → hand off to <SiteRouterV2 />.
 */
import React, { useState, useEffect } from "react";
import api from "../api/client";
import ComingSoonPlaceholder from "./ComingSoonPlaceholder";
import { usePublicSite } from "../contexts/PublicSiteContext";
import { isDevHostname } from "../config/platform";

const PLATFORM_DOMAIN = process.env.REACT_APP_PLATFORM_DOMAIN || "partyrentalsuite.com";

const SiteRouterV2 = React.lazy(() => import("./SiteRouterV2"));

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Tenant subdomain (joes.partyrentalsuite.com) or custom domain (www.joesbounce.com) → label to resolve; null on platform/dev hosts. */
function getSubdomain() {
  const host = window.location.hostname.toLowerCase();
  const platform = PLATFORM_DOMAIN.toLowerCase();
  if (isDevHostname(host)) return null;
  if (host === platform || host === `www.${platform}` || host === `app.${platform}`) return null;
  if (host.endsWith(`.${platform}`)) {
    const prefix = host.slice(0, -(platform.length + 1));
    if (prefix && prefix !== "www" && prefix !== "app") return prefix;
    return null;
  }
  return host; // custom domain — resolved by hostname via /api/public/site/resolve
}

export default function SubdomainRouter({ children }) {
  const subdomain = getSubdomain();
  const { configure } = usePublicSite();
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(!!subdomain);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!subdomain) return;
    const host = window.location.hostname;
    api.get(`/api/public/site/resolve?domain=${encodeURIComponent(host)}`)
      .then((res) => {
        const data = res.data;
        setResolved(data);
        localStorage.setItem("public_tenant_id", data.tenant_id);
        localStorage.setItem("public_microsite_id", data.microsite_id);
        api.defaults.headers.common["X-Tenant-ID"] = data.tenant_id;
        api.defaults.headers.common["X-Microsite-ID"] = data.microsite_id;
        configure(data.tenant_id, data.microsite_id);
      })
      .catch(() => setError("Site not found"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomain]);

  // Not a tenant subdomain — render normal app
  if (!subdomain) return children;

  if (loading) return <Loader />;

  if (error || !resolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="subdomain-router-not-found">
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Site Not Found</h1>
          <p className="text-sm text-slate-500 mb-4">
            The site <strong>{subdomain.includes(".") ? subdomain : `${subdomain}.${PLATFORM_DOMAIN}`}</strong> doesn't exist or hasn't been set up yet.
          </p>
          <a href={`https://${PLATFORM_DOMAIN}`} className="text-sm text-blue-600 hover:underline">
            Go to {PLATFORM_DOMAIN}
          </a>
        </div>
      </div>
    );
  }

  // Let admin, platform, auth, and utility routes pass through to normal app routing
  const path = window.location.pathname;
  if (
    path.startsWith("/admin") ||
    path.startsWith("/platform") ||
    path.startsWith("/signup") ||
    path.startsWith("/verify-email") ||
    path.startsWith("/onboarding") ||
    path.startsWith("/invoice/") ||
    path.startsWith("/quote/") ||
    path.startsWith("/account")
  ) {
    return children;
  }

  // Draft gate.
  if (resolved.status === "draft") {
    const hasAdminJwt = !!(localStorage.getItem("access_token") || localStorage.getItem("tenant_token"));
    const previewParam = new URLSearchParams(window.location.search).get("preview") === "true";
    if (!hasAdminJwt && !previewParam) {
      return <ComingSoonPlaceholder resolved={resolved} />;
    }
  }

  // Render the tenant site through the token-driven v2 engine.
  return (
    <React.Suspense fallback={<Loader />}>
      <SiteRouterV2 siteData={resolved} subdomain={subdomain} />
    </React.Suspense>
  );
}
